import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CctvDtoKeysEnum } from '@zaoblako/shared/modules/cctv/enums/cctv-dto-keys.enum';
import { entityLikeSchema, entityLikeSchemaStrict } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { CctvCheckersEnum } from '@zaoblako/shared/modules/cctv/enums/cctv-checkers.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import type { CarCategoryFindInterface } from '@zaoblako/shared/modules/cctv/dtos/car-category/car-category-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';

import * as yup from 'yup';

/** Transit task create/update request body */
export interface TransitTaskFormInterface {
  /** Transit day */
  date: Date;
  /** State registration number of the car */
  carNumber: string;
  /** Car category */
  category: CarCategoryFindInterface;
  /** Responsible (internal user) */
  responsible?: UserFindInterface;
  /** External user */
  externalUser?: ExternalUserFindInterface;
  /** Client */
  client?: ClientFindInterface;
  /** Auditors (internal users) */
  userAuditors?: UserFindInterface[];
  /** Auditor groups (internal users) */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Rooms to which access is granted */
  rooms: RoomFindInterface[];
}

/** Transit task create/update request body - DTO schema */
export const TransitTaskFormDto = SerdeDtoClass.create<TransitTaskFormInterface>(CctvDtoKeysEnum.TRANSIT_TASK_FORM_DTO, {
  date: {
    label: 'Дата проезда',
    serializer: yup.date(),
  },
  carNumber: {
    label: 'Номер ТС',
    serializer: nonEmptyStringSchema,
  },
  category: {
    label: 'Категория ТС',
    serializer: entityLikeSchemaStrict,
    checkers: [CctvCheckersEnum.CAR_CATEGORY_ASSERT_EXISTS_BY_DTO],
  },
  responsible: {
    optional: true,
    label: 'Ответственный',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  externalUser: {
    optional: true,
    label: 'Внешний пользователь',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.EXTERNAL_USER_ASSERT_EXISTS_BY_DTO],
  },
  client: {
    optional: true,
    label: 'Клиент',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.CLIENT_ASSERT_EXISTS_BY_DTO],
  },
  userAuditors: {
    optional: true,
    label: 'Аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  userGroupAuditors: {
    optional: true,
    label: 'Группы аудиторов',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
  },
  rooms: {
    label: 'Помещения',
    serializer: yup
      .array(entityLikeSchema)
      .min(1, DtoMessageEnum.EMPTY_ARRAY),
    checkers: [CatalogCheckersEnum.ROOM_ASSERT_EXISTS_BY_DTO],
  },
});
